import React from "react";
import { Box } from "@mui/material";
import { certificatePageSize } from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";

/** How far in from the page edge the outer rule runs. */
const RULE_INSET = 36;
/** The fine inner rule, just inside the outer one. */
const INNER_RULE_GAP = 8;
/** How far from each corner the outer rule stops and curls into a scroll. */
const SCROLL_REACH = 60;
/** The inner rule stops this much further from each corner, clear of the scrolls. */
const INNER_RULE_SHORTFALL = SCROLL_REACH + 24;

/** The scroll's middle half-circle, which bulges furthest from the corner. */
const SCROLL_SWELL = 10;

/**
 * A spiral of shrinking half-circles, starting from the end of a rule at
 * (SCROLL_REACH, 0) heading towards the corner and curling in beside it.
 */
const SCROLL = `M${SCROLL_REACH},0 A14,14 0 0 0 ${SCROLL_REACH},28 A${SCROLL_SWELL},${SCROLL_SWELL} 0 0 0 ${SCROLL_REACH},8 A6,6 0 0 0 ${SCROLL_REACH},20`;

/** How far in from each page edge the corner scrolls reach, along the rule. */
const CORNER_REACH = RULE_INSET + SCROLL_REACH + SCROLL_SWELL;

/**
 * One corner's ornament, drawn for the top-left with the corner at the origin:
 * a scroll on each rule's end, a curve joining them and a small diamond in the
 * corner.
 */
const CornerScroll: React.FC<{
  transform: string;
  primary: string;
  accent: string;
}> = ({ transform, primary, accent }) => (
  <g transform={transform}>
    <g fill="none" stroke={primary} strokeWidth={2.5} strokeLinecap="round">
      <path d={SCROLL} />
      {/* The same scroll mirrored across the diagonal, for the side rule. */}
      <path d={SCROLL} transform="matrix(0 1 1 0 0 0)" />
    </g>
    <path
      d={`M${SCROLL_REACH - 14},14 C30,14 14,30 14,${SCROLL_REACH - 14}`}
      fill="none"
      stroke={primary}
      strokeWidth={1.5}
    />
    <polygon points="10,2 18,10 10,18 2,10" fill={accent} />
  </g>
);

/** The outer rule ending in scrolls at each corner, and a fine inner rule. */
const ScrolledBorder: React.FC<{
  width: number;
  height: number;
  primary: string;
  accent: string;
}> = ({ width, height, primary, accent }) => {
  const left = RULE_INSET;
  const top = RULE_INSET;
  const right = width - RULE_INSET;
  const bottom = height - RULE_INSET;
  /** One rule round all four sides, stopping short of each corner. */
  const sides = (inset: number, shortfall: number) =>
    [
      [left + shortfall, top + inset, right - shortfall, top + inset],
      [left + shortfall, bottom - inset, right - shortfall, bottom - inset],
      [left + inset, top + shortfall, left + inset, bottom - shortfall],
      [right - inset, top + shortfall, right - inset, bottom - shortfall],
    ].map(([x1, y1, x2, y2]) => `M${x1},${y1} L${x2},${y2}`);

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      sx={{ position: "absolute", inset: 0, width, height }}
    >
      {sides(0, SCROLL_REACH).map((d) => (
        <path key={d} d={d} stroke={primary} strokeWidth={2.5} />
      ))}
      {sides(INNER_RULE_GAP, INNER_RULE_SHORTFALL).map((d) => (
        <path key={d} d={d} stroke={accent} strokeWidth={1} />
      ))}
      {[
        `translate(${left} ${top})`,
        `translate(${right} ${top}) scale(-1 1)`,
        `translate(${right} ${bottom}) scale(-1 -1)`,
        `translate(${left} ${bottom}) scale(1 -1)`,
      ].map((transform) => (
        <CornerScroll
          key={transform}
          transform={transform}
          primary={primary}
          accent={accent}
        />
      ))}
    </Box>
  );
};

/**
 * Ornate: a rule that curls into scrolls at every corner, with a fine inner
 * rule, like a festival or elocution certificate. Its suggested look sets an
 * Old English title.
 */
export const ornateDesign: CertificateDesign = {
  bodyFont: "lora",
  Layout: ({ template, text, orientation }) => {
    const { width, height } = certificatePageSize(orientation);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        // Clear of the corner scrolls: landscape by its sides, portrait by
        // keeping the footer above the bottom ones.
        inset={{
          landscape: `76px ${CORNER_REACH + 18}px 68px`,
          portrait: "84px 100px 76px",
        }}
        frame={
          <ScrolledBorder
            width={width}
            height={height}
            primary={template.primaryColour}
            accent={template.accentColour}
          />
        }
      />
    );
  },
};
